import { useState, useEffect } from 'react';
import { Button, Card, Badge, Spinner, Table, Form, InputGroup } from 'react-bootstrap';
import { BookOpen, Plus, Eye, Edit2, Trash2, Search, History } from 'lucide-react';
import { useAgentRegistry } from '../../../apps/hooks/agent/useAgentRegistry';
import type { ManualResponse } from '../../../infrastructure/AIAgentRegistryClient';
import { ManualDetailModal } from './ManualDetailModal';
import { ManualHistoryModal } from './ManualHistoryModal';
import { UpsertManualModal } from './UpsertManualModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { formatDate } from '../../../utils/dateUtils';

export const ManualTab = () => {
  const { categoriesQuery, useManualsQuery, upsertManualMutation, deleteManualMutation } = useAgentRegistry();
  const { data: categories = [], isLoading: isLoadingCategories } = categoriesQuery;
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedManual, setSelectedManual] = useState<ManualResponse | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showUpsertModal, setShowUpsertModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingManual, setEditingManual] = useState<ManualResponse | null>(null);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory && categories[0]) {
      setSelectedCategory(categories[0].name);
    }
  }, [categories, selectedCategory]);

  const manualsQuery = useManualsQuery(selectedCategory);
  const { data: manuals = [], isLoading } = manualsQuery;

  const handleViewManual = (manual: ManualResponse) => {
    setSelectedManual(manual);
    setShowDetailModal(true);
  };

  const handleViewHistory = (manual: ManualResponse) => {
    setSelectedManual(manual);
    setShowHistoryModal(true);
  };

  const handleCreateManual = () => {
    setEditingManual(null);
    setShowUpsertModal(true);
  };

  const handleEditManual = (manual: ManualResponse) => {
    setEditingManual(manual);
    setShowUpsertModal(true);
  };

  const handleDeleteManual = (manual: ManualResponse) => {
    setSelectedManual(manual);
    setShowDeleteModal(true);
  };

  const handleRestoreVersion = (version: ManualResponse) => {
    const restoredVersion: ManualResponse = {
      ...version,
    };
    setEditingManual(restoredVersion);
    setShowHistoryModal(false);
    setShowUpsertModal(true);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <BookOpen className="me-2" size={20} />
          Manuals
        </h5>
        <Button variant="primary" size="sm" onClick={handleCreateManual}>
          <Plus size={16} className="me-1" /> New Manual
        </Button>
      </div>

      <div className="mb-4">
        <InputGroup>
          <InputGroup.Text><Search size={16} /></InputGroup.Text>
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={isLoadingCategories || categories.length === 0}
          >
            {categories.length === 0 ? (
              <option>Loading categories...</option>
            ) : (
              categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                </option>
              ))
            )}
          </Form.Select>
        </InputGroup>
      </div>

      {isLoadingCategories ? (
        <div className="text-center my-5"><Spinner animation="border" /></div>
      ) : !selectedCategory ? (
        <Card body className="text-center text-muted">
          Please select a category.
        </Card>
      ) : isLoading ? (
        <div className="text-center my-5"><Spinner animation="border" /></div>
      ) : manuals.length === 0 ? (
        <Card body className="text-center text-muted">
          No manuals found in "{selectedCategory}" category.
        </Card>
      ) : (
        <Table hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Category</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {manuals.map((manual) => (
              <tr key={manual.id} onClick={() => handleViewManual(manual)} style={{ cursor: 'pointer' }}>
                <td><strong>{manual.name}</strong></td>
                <td className="text-truncate" style={{ maxWidth: '300px' }}>{manual.description}</td>
                <td><Badge bg="secondary">{manual.category}</Badge></td>
                <td>{formatDate(manual.updatedAt)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline-info" size="sm" className="me-1" onClick={() => handleViewManual(manual)}><Eye size={14} /></Button>
                  <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleViewHistory(manual)}><History size={14} /></Button>
                  <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleEditManual(manual)}><Edit2 size={14} /></Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeleteManual(manual)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <ManualDetailModal
        show={showDetailModal}
        manual={selectedManual}
        onHide={() => setShowDetailModal(false)}
        onEdit={() => {
          setShowDetailModal(false);
          if (selectedManual) handleEditManual(selectedManual);
        }}
      />

      <ManualHistoryModal
        show={showHistoryModal}
        manualId={selectedManual?.id ?? null}
        manualName={selectedManual?.name ?? ''}
        onHide={() => setShowHistoryModal(false)}
        onRestore={handleRestoreVersion}
      />

      <UpsertManualModal
        show={showUpsertModal}
        manual={editingManual}
        defaultCategory={selectedCategory}
        onHide={() => setShowUpsertModal(false)}
        onSubmit={(data) => {
          upsertManualMutation.mutate(data);
          setShowUpsertModal(false);
        }}
        isSubmitting={upsertManualMutation.isPending}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        title="Delete Manual"
        message={`Are you sure you want to delete "${selectedManual?.name}"? This action cannot be undone.`}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (selectedManual) deleteManualMutation.mutate(selectedManual.id);
          setShowDeleteModal(false);
        }}
        isDeleting={deleteManualMutation.isPending}
      />
    </>
  );
};

